import { plainDecimal } from './money';
import type { ExportKind, ExportMode, JournalLine } from './exportTypes';

/**
 * The Payroll Accounting Export CSV, schema version 1 (contract section 10). The REAL file is always produced by the server; this builder
 * exists so the MOCK gateway hands out a file with the same documented shape, and so the tests and the end-to-end spec can pin the header row.
 * UTF-8 without a byte-order mark, CRLF line endings, comma separator, RFC 4180 quoting, two-decimal amounts with no grouping, the
 * non-applicable side left empty, and formula-injection protection on every text cell.
 */
export const CSV_SCHEMA_VERSION = 1;

export const CSV_COLUMNS = [
  'ExportId', 'CompanyId', 'PayrollRunId', 'PayPeriodStart', 'PayPeriodEnd', 'CheckDate', 'AccountingDate', 'JournalEntryReference', 'LineNumber',
  'GlAccountNumber', 'GlAccountName', 'Debit', 'Credit', 'Description', 'EmployeeReference', 'Department', 'Location', 'ClassDimension', 'CheckReference',
  'PayrollSourceCategory', 'MappingVersion', 'ReversalReference',
] as const;

export const CSV_HEADER_ROW = CSV_COLUMNS.join(',');

/** A text cell whose first character could start a spreadsheet formula is prefixed with a single quote; the cell is never a formula. */
export function protectText(value: string): string {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

/** RFC 4180: a cell holding a comma, a quote or a line break is quoted, with inner quotes doubled. */
export function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

const text = (value: string | null | undefined): string => csvCell(protectText(value ?? ''));

export interface CsvExportHeader {
  id: string;
  companyId: string;
  payrollRunId: string;
  kind: ExportKind;
  mode: ExportMode;
  periodStart: string;
  periodEnd: string;
  checkDate: string;
  accountingDate: string;
  journalReference: string;
  /** The original export's journal reference, for the lines of a Reversal. */
  reversalReference: string | null;
}

export function buildCsv(header: CsvExportHeader, lines: readonly JournalLine[]): string {
  const rows = [CSV_HEADER_ROW];
  for (const line of lines) {
    const category = line.subcategory ? `${line.category}/${line.subcategory}` : line.category;
    rows.push([
      header.id, header.companyId, header.payrollRunId, header.periodStart, header.periodEnd, header.checkDate, header.accountingDate,
      text(header.journalReference), String(line.lineNumber), text(line.accountNumber), text(line.accountName),
      line.side === 'Debit' ? plainDecimal(line.amountCents) : '', line.side === 'Credit' ? plainDecimal(line.amountCents) : '',
      text(line.description),
      // The employee number only (never a name, SSN or bank detail), and only in a per-employee export or on the per-employee net pay lines of a compound one.
      header.mode === 'PerEmployee' || line.category === 'NetPayClearing' ? text(line.employeeReference) : '',
      text(line.department), text(line.location), text(line.classDimension), text(line.checkReference), text(category),
      String(line.mappingVersion), header.kind === 'Reversal' ? text(header.reversalReference) : '',
    ].join(','));
  }
  return `${rows.join('\r\n')}\r\n`;
}
